import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  Tabs,
  Tab
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Store as StoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { isWireTransferEnabled } from '../utils/featureFlags';
import api, { handleApiError } from '../utils/api';

const AdminPaymentsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [classPayments, setClassPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [classSummary, setClassSummary] = useState({});
  const [pagination, setPagination] = useState({});
  const [classPagination, setClassPagination] = useState({});
  const [error, setError] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    paymentMethod: '',
    dateFrom: null,
    dateTo: null,
    minAmount: '',
    maxAmount: '',
    userType: '',
    search: '',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadPayments();
    loadClassPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  const loadPayments = async () => {
    setLoading(true);
    setError('');

    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          if (key === 'dateFrom' || key === 'dateTo') {
            if (value instanceof Date) {
              // Set to start/end of day in local timezone
              const adjustedDate = new Date(value);
              if (key === 'dateFrom') {
                adjustedDate.setHours(0, 0, 0, 0);
              } else {
                adjustedDate.setHours(23, 59, 59, 999);
              }
              queryParams.append(key, adjustedDate.toISOString());
            }
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      const response = await api.get(`/admin/payments?${queryParams.toString()}`);
      
      if (response.data.success) {
        // Filter out class payments (CLS- tokens) from registration payments
        const allPayments = response.data.data.payments;
        const registrationPaymentsOnly = allPayments.filter(payment => 
          !payment.paymentToken?.startsWith('CLS-')
        );
        
        setPayments(registrationPaymentsOnly);
        setSummary(response.data.data.summary);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to load payments:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadClassPayments = async () => {
    try {
      const queryParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          if (key === 'dateFrom' || key === 'dateTo') {
            if (value instanceof Date) {
              // Set to start/end of day in local timezone
              const adjustedDate = new Date(value);
              if (key === 'dateFrom') {
                adjustedDate.setHours(0, 0, 0, 0);
              } else {
                adjustedDate.setHours(23, 59, 59, 999);
              }
              queryParams.append(key, adjustedDate.toISOString());
            }
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      const response = await api.get(`/admin/payments/class?${queryParams.toString()}`);
      
      if (response.data.success) {
        setClassPayments(response.data.data.payments);
        setClassSummary(response.data.data.summary);
        setClassPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to load class payments:', error);
      setError(handleApiError(error));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleApplyFilters = () => {
    loadPayments();
    loadClassPayments();
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      paymentMethod: '',
      dateFrom: null,
      dateTo: null,
      minAmount: '',
      maxAmount: '',
      userType: '',
      search: '',
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    loadPayments();
    loadClassPayments();
  };

  const handlePageChange = (event, newPage) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    setFilters(prev => ({ 
      ...prev, 
      limit: parseInt(event.target.value, 10),
      page: 1 
    }));
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setDetailsOpen(true);
  };

  const toggleRowExpansion = (paymentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(paymentId)) {
      newExpanded.delete(paymentId);
    } else {
      newExpanded.add(paymentId);
    }
    setExpandedRows(newExpanded);
  };

  const exportToCSV = async () => {
    try {
      setExportLoading(true);
      setError(''); // Clear any previous errors
      console.log('Starting CSV export...');
      
      // Get all payments with current filters (no pagination)
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined && key !== 'page' && key !== 'limit') {
          if (key === 'dateFrom' || key === 'dateTo') {
            if (value instanceof Date && !isNaN(value)) {
              queryParams.append(key, value.toISOString());
            }
          } else if (key === 'minAmount' || key === 'maxAmount') {
            // Ensure numeric values are valid
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue >= 0) {
              queryParams.append(key, numValue.toString());
            }
          } else if (key === 'search') {
            // Ensure search term is not empty and within limits
            const searchTerm = value.toString().trim();
            if (searchTerm.length > 0 && searchTerm.length <= 100) {
              queryParams.append(key, searchTerm);
            }
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      // Set a high limit to get all results
      queryParams.append('limit', '10000');
      queryParams.append('page', '1');

      console.log('Export query params:', queryParams.toString());

      const endpoint = currentTab === 0 ? `/admin/payments` : `/admin/payments/class`;
      const response = await api.get(`${endpoint}?${queryParams.toString()}`);
      console.log('Export API response:', response.data);
      
      if (response.data.success) {
        let allPayments = response.data.data.payments;
        
        // Filter out class payments (CLS- tokens) from registration payments export
        if (currentTab === 0) {
          allPayments = allPayments.filter(payment => 
            !payment.paymentToken?.startsWith('CLS-')
          );
        }
        
        console.log(`Exporting ${allPayments.length} payments`);
        
        if (allPayments.length === 0) {
          setError('No payments found to export with current filters');
          return;
        }
        
        // Convert to CSV
        const csvHeaders = currentTab === 0 ? [
          'Payment Token',
          'Amount',
          'Currency',
          'Status',
          'Payment Method',
          'User Name',
          'User Email',
          'TCKN',
          'Address',
          'User Type',
          'Plan',
          'Created Date',
          'Paid Date',
          'Transaction ID',
          'Reference Code',
          'Failure Reason'
        ] : [
          'Payment Token',
          'Amount',
          'Currency',
          'Status',
          'Payment Method',
          'User Name',
          'User Email',
          'TCKN',
          'Address',
          'User Type',
          'Class Name',
          'Class Date',
          'Class Time',
          'Facility',
          'Created Date',
          'Paid Date',
          'Gateway Transaction ID',
          'Notes'
        ];

        const csvRows = allPayments.map(payment => {
          // Helper function to format address
          const formatAddress = (address) => {
            if (!address) return '';
            const parts = [
              address.street,
              address.district,
              address.city,
              address.postalCode
            ].filter(Boolean);
            return parts.join(', ');
          };

          if (currentTab === 0) {
            return [
              payment.paymentToken || '',
              payment.amount || 0,
              payment.currency || 'TRY',
              payment.status || '',
              payment.paymentMethod ? payment.paymentMethod.replace('_', ' ') : '',
              payment.user ? `${payment.user.firstName || ''} ${payment.user.lastName || ''}`.trim() : '',
              payment.user?.email || '',
              payment.user?.tckn || '',
              formatAddress(payment.user?.address),
              payment.user?.memberType?.name || '',
              payment.registration?.selectedPlan?.name || '',
              payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : '',
              payment.paidAt ? new Date(payment.paidAt).toLocaleDateString() : '',
              payment.creditCardDetails?.transactionId || payment.wireTransferDetails?.referenceCode || payment.inPlaceDetails?.receiptNumber || '',
              payment.wireTransferDetails?.referenceCode || '',
              payment.failureReason || ''
            ];
          } else {
            return [
              payment.paymentToken || '',
              payment.amount || 0,
              payment.currency || 'TRY',
              payment.status || '',
              payment.paymentMethod ? payment.paymentMethod.replace('_', ' ') : '',
              payment.user ? `${payment.user.firstName || ''} ${payment.user.lastName || ''}`.trim() : '',
              payment.user?.email || '',
              payment.user?.tckn || '',
              formatAddress(payment.user?.address),
              payment.user?.memberType?.name || '',
              payment.scheduledClass?.classType?.name || '',
              payment.scheduledClass?.startTime ? new Date(payment.scheduledClass.startTime).toLocaleDateString() : '',
              payment.scheduledClass?.startTime ? new Date(payment.scheduledClass.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
              payment.scheduledClass?.facility?.name || '',
              payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : '',
              payment.paidAt ? new Date(payment.paidAt).toLocaleDateString() : '',
              payment.gatewayTransactionId || '',
              payment.notes || ''
            ];
          }
        });

        const csvContent = [csvHeaders, ...csvRows]
          .map(row => row.map(field => {
            // Handle potential special characters and quotes
            const cleanField = String(field).replace(/"/g, '""');
            return `"${cleanField}"`;
          }).join(','))
          .join('\n');

        console.log('CSV content prepared, creating download...');

        // Create and download file
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for UTF-8
        const link = document.createElement('a');
        
        const fileType = currentTab === 0 ? 'registration' : 'class';
        const fileName = `${fileType}-payments-export-${new Date().toISOString().split('T')[0]}.csv`;
        
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          // For IE/Edge
          window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
          // For other browsers
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', fileName);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url); // Clean up
        }
        
        console.log('CSV export completed successfully');
      } else {
        throw new Error(response.data.error || 'API returned unsuccessful response');
      }
    } catch (error) {
      console.error('Export failed:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = handleApiError(error);
      if (error.response?.data?.details) {
        errorMessage = handleApiError(error);
        if (error.response.data.details) {
          console.error('Validation details:', error.response.data.details);
          const validationErrors = error.response.data.details.map(detail => detail.msg).join(', ');
          errorMessage += `: ${validationErrors}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(`Export failed: ${errorMessage}`);
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <SuccessIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'pending': case 'processing': return <PendingIcon color="warning" />;
      case 'cancelled': return <CancelIcon color="disabled" />;
      default: return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'pending': case 'processing': return 'warning';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'credit_card': return <CreditCardIcon />;
      case 'wire_transfer': return <BankIcon />;
      case 'in_place': return <StoreIcon />;
      default: return <CreditCardIcon />;
    }
  };

  const formatAmount = (amount, currency = 'TRY') => {
    return `${amount.toLocaleString()} ${currency}`;
  };

  if (loading && payments.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {t('admin:payments.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('admin:payments.subtitle')}
          </Typography>
          
          {/* Tabs */}
          <Box sx={{ mt: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
              <Tab label={t('admin:payments.tabs.registrationPayments')} />
              <Tab label={t('admin:payments.tabs.classPayments')} />
            </Tabs>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  {t('admin:payments.summary.totalPayments')}
                </Typography>
                <Typography variant="h4">
                  {currentTab === 0 ? (summary.totalPayments || 0) : (classSummary.totalPayments || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentTab === 0 ? t('admin:payments.summary.registrationPayments') : t('admin:payments.summary.classBookingPayments')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main" gutterBottom>
                  {t('admin:payments.summary.totalRevenue')}
                </Typography>
                <Typography variant="h4">
                  {formatAmount(currentTab === 0 ? (summary.completedAmount || 0) : (classSummary.completedAmount || 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:payments.summary.completedOnly')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main" gutterBottom>
                  {t('admin:payments.summary.pending')}
                </Typography>
                <Typography variant="h4">
                  {currentTab === 0 
                    ? ((summary.statusBreakdown?.pending || 0) + (summary.statusBreakdown?.processing || 0))
                    : ((classSummary.statusBreakdown?.pending || 0) + (classSummary.statusBreakdown?.processing || 0))
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:payments.summary.awaitingCompletion')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main" gutterBottom>
                  {t('admin:payments.summary.failed')}
                </Typography>
                <Typography variant="h4">
                  {currentTab === 0 
                    ? (summary.statusBreakdown?.failed || 0)
                    : ((classSummary.statusBreakdown?.failed || 0) + (classSummary.statusBreakdown?.expired || 0))
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentTab === 0 ? t('admin:payments.summary.paymentFailures') : t('admin:payments.summary.failedExpired')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Actions Bar */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              {t('admin:payments.actions.filters')}
            </Button>
            <TextField
              size="small"
              placeholder={t('admin:payments.filters.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadPayments()}
              sx={{ minWidth: 250 }}
            />
            <Button
              variant="outlined"
              onClick={loadPayments}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              {t('admin:payments.actions.refresh')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={exportToCSV}
              disabled={exportLoading}
            >
              {exportLoading ? t('admin:payments.actions.exporting') : t('admin:payments.actions.exportCSV')}
            </Button>
          </Box>
        </Box>

        {/* Filters Panel */}
        <Collapse in={filtersOpen}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('admin:payments.filters.advancedFilters')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label={t('admin:payments.filters.status')}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">{t('admin:payments.filters.allStatus')}</MenuItem>
                    <MenuItem value="pending">{t('admin:payments.filters.pending')}</MenuItem>
                    <MenuItem value="processing">{t('admin:payments.filters.processing')}</MenuItem>
                    <MenuItem value="completed">{t('admin:payments.filters.completed')}</MenuItem>
                    <MenuItem value="failed">{t('admin:payments.filters.failed')}</MenuItem>
                    {currentTab === 1 && <MenuItem value="expired">{t('admin:payments.filters.expired')}</MenuItem>}
                    <MenuItem value="cancelled">{t('admin:payments.filters.cancelled')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={filters.paymentMethod}
                    label={t('admin:payments.filters.paymentMethod')}
                    onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  >
                    <MenuItem value="">{t('admin:payments.filters.allMethods')}</MenuItem>
                    <MenuItem value="credit_card">{t('admin:payments.filters.creditCard')}</MenuItem>
                    {isWireTransferEnabled() && (
                      <MenuItem value="wire_transfer">{t('admin:payments.filters.wireTransfer')}</MenuItem>
                    )}
                    <MenuItem value="in_place">{t('admin:payments.filters.payAtGym')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>User Type</InputLabel>
                  <Select
                    value={filters.userType}
                    label={t('admin:payments.filters.userType')}
                    onChange={(e) => handleFilterChange('userType', e.target.value)}
                  >
                    <MenuItem value="">{t('admin:payments.filters.allTypes')}</MenuItem>
                    <MenuItem value="student">{t('admin:payments.filters.student')}</MenuItem>
                    <MenuItem value="technopolis_employee">{t('admin:payments.filters.technopolisEmployee')}</MenuItem>
                    <MenuItem value="external">{t('admin:payments.filters.external')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label={t('admin:payments.filters.dateFrom')}
                  value={filters.dateFrom}
                  onChange={(date) => handleFilterChange('dateFrom', date)}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label={t('admin:payments.filters.dateTo')}
                  value={filters.dateTo}
                  onChange={(date) => handleFilterChange('dateTo', date)}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('admin:payments.filters.minAmount')}
                  type="number"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('admin:payments.filters.maxAmount')}
                  type="number"
                  value={filters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleApplyFilters}>
                {t('admin:payments.filters.applyFilters')}
              </Button>
              <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                {t('admin:payments.filters.clearAll')}
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {/* Payments Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin:payments.table.headers.payment')}</TableCell>
                  <TableCell>{t('admin:payments.table.headers.user')}</TableCell>
                  <TableCell>TCKN</TableCell>
                  <TableCell>{t('admin:payments.table.headers.address')}</TableCell>
                  <TableCell>{t('admin:payments.table.headers.amount')}</TableCell>
                  <TableCell>{t('admin:payments.table.headers.method')}</TableCell>
                  <TableCell>{t('admin:payments.table.headers.status')}</TableCell>
                  <TableCell>{t('admin:payments.table.headers.date')}</TableCell>
                  {currentTab === 1 && <TableCell>{t('admin:payments.table.headers.class')}</TableCell>}
                  <TableCell>{t('admin:payments.table.headers.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(currentTab === 0 ? payments : classPayments).map((payment) => (
                  <React.Fragment key={payment.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {payment.paymentToken}
                          </Typography>
                          {currentTab === 0 && payment.registration?.applicationId && (
                            <Typography variant="caption" color="text.secondary">
                              App: {payment.registration.applicationId}
                            </Typography>
                          )}
                          {currentTab === 1 && (
                            <Typography variant="caption" color="text.secondary">
                              Class: {payment.paymentToken}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {payment.user ? (
                          <Box>
                            <Typography variant="body2">
                              {payment.user.firstName} {payment.user.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {payment.user.email}
                            </Typography>
                            {payment.user.memberType && (
                              <Chip
                                label={payment.user.memberType.name}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1, mt: 0.5 }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('admin:payments.table.userNotFound')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {payment.user?.tckn ? (
                          <Typography variant="body2">
                            {payment.user.tckn}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {payment.user?.address ? (
                          <Box>
                            <Typography variant="body2">
                              {[
                                payment.user.address.street,
                                payment.user.address.district,
                                payment.user.address.city
                              ].filter(Boolean).join(', ')}
                            </Typography>
                            {payment.user.address.postalCode && (
                              <Typography variant="caption" color="text.secondary">
                                {payment.user.address.postalCode}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatAmount(payment.amount, payment.currency)}
                        </Typography>
                        {currentTab === 0 && payment.registration?.selectedPlan && (
                          <Typography variant="caption" color="text.secondary">
                            {payment.registration.selectedPlan.name}
                          </Typography>
                        )}
                        {currentTab === 1 && payment.scheduledClass?.classType && (
                          <Typography variant="caption" color="text.secondary">
                            {payment.scheduledClass.classType.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getPaymentMethodIcon(payment.paymentMethod)}
                          <Typography variant="body2">
                            {payment.paymentMethod.replace('_', ' ').toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(payment.status)}
                          <Chip
                            label={payment.status.toUpperCase()}
                            color={getStatusColor(payment.status)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(payment.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(payment.createdAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      {currentTab === 1 && (
                        <TableCell>
                          {payment.scheduledClass ? (
                            <Box>
                              <Typography variant="body2">
                                {payment.scheduledClass.classType?.name || 'Unknown Class'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(payment.scheduledClass.startTime).toLocaleDateString()} at{' '}
                                {new Date(payment.scheduledClass.startTime).toLocaleTimeString([], { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </Typography>
                              {payment.scheduledClass.facility && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {payment.scheduledClass.facility.name}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('admin:payments.table.classNotFound')}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={t('admin:payments.actions.viewDetails')}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewDetails(payment)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={expandedRows.has(payment.id) ? t('admin:payments.actions.collapse') : t('admin:payments.actions.expand')}>
                            <IconButton 
                              size="small" 
                              onClick={() => toggleRowExpansion(payment.id)}
                            >
                              {expandedRows.has(payment.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Row Content */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={currentTab === 1 ? 10 : 9}>
                        <Collapse in={expandedRows.has(payment.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              {t('admin:payments.details.title')}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  {t('admin:payments.details.transactionInfo')}
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                  {currentTab === 0 && payment.creditCardDetails?.transactionId && (
                                    <Typography variant="body2">
                                      <strong>{t('admin:payments.details.transactionId')}:</strong> {payment.creditCardDetails.transactionId}
                                    </Typography>
                                  )}
                                  {currentTab === 1 && payment.gatewayTransactionId && (
                                    <Typography variant="body2">
                                      <strong>{t('admin:payments.details.gatewayTransactionId')}:</strong> {payment.gatewayTransactionId}
                                    </Typography>
                                  )}
                                  {currentTab === 0 && payment.wireTransferDetails?.referenceCode && (
                                    <Typography variant="body2">
                                      <strong>{t('admin:payments.details.referenceCode')}:</strong> {payment.wireTransferDetails.referenceCode}
                                    </Typography>
                                  )}
                                  {payment.paidAt && (
                                    <Typography variant="body2">
                                      <strong>{t('admin:payments.details.paidAt')}:</strong> {new Date(payment.paidAt).toLocaleString()}
                                    </Typography>
                                  )}
                                  {payment.failedAt && currentTab === 1 && (
                                    <Typography variant="body2">
                                      <strong>Failed At:</strong> {new Date(payment.failedAt).toLocaleString()}
                                    </Typography>
                                  )}
                                  {payment.expiresAt && (
                                    <Typography variant="body2">
                                      <strong>Expires At:</strong> {new Date(payment.expiresAt).toLocaleString()}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, md: 6 }}>
                                {payment.failureReason && (
                                  <>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Failure Information
                                    </Typography>
                                    <Box sx={{ pl: 2 }}>
                                      <Typography variant="body2" color="error">
                                        {payment.failureReason}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                                {(currentTab === 0 ? payment.adminNotes : payment.notes) && (
                                  <>
                                    <Typography variant="subtitle2" gutterBottom>
                                      {currentTab === 0 ? 'Admin Notes' : 'Notes'}
                                    </Typography>
                                    <Box sx={{ pl: 2 }}>
                                      <Typography variant="body2">
                                        {currentTab === 0 ? payment.adminNotes : payment.notes}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={currentTab === 0 ? (pagination.totalCount || 0) : (classPagination.totalCount || 0)}
            rowsPerPage={filters.limit}
            page={currentTab === 0 
              ? (pagination.currentPage ? pagination.currentPage - 1 : 0)
              : (classPagination.currentPage ? classPagination.currentPage - 1 : 0)
            }
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Paper>

        {/* Payment Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {t('admin:payments.dialog.title', { token: selectedPayment?.paymentToken })}
          </DialogTitle>
          <DialogContent>
            {selectedPayment && (
              <Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Basic Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary={t('admin:payments.table.headers.amount')}
                          secondary={formatAmount(selectedPayment.amount, selectedPayment.currency)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('admin:payments.table.headers.status')}
                          secondary={selectedPayment.status.toUpperCase()}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('admin:payments.table.headers.method')}
                          secondary={selectedPayment.paymentMethod.replace('_', ' ').toUpperCase()}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('admin:payments.table.headers.created')}
                          secondary={new Date(selectedPayment.createdAt).toLocaleString()}
                        />
                      </ListItem>
                      {selectedPayment.paidAt && (
                        <ListItem>
                          <ListItemText
                            primary={t('admin:payments.table.headers.paidAt')}
                            secondary={new Date(selectedPayment.paidAt).toLocaleString()}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      User Information
                    </Typography>
                    {selectedPayment.user ? (
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary={t('admin:payments.table.headers.name')}
                            secondary={`${selectedPayment.user.firstName} ${selectedPayment.user.lastName}`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={t('admin:payments.table.headers.email')}
                            secondary={selectedPayment.user.email}
                          />
                        </ListItem>
                        {selectedPayment.user.memberType && (
                          <ListItem>
                            <ListItemText
                              primary={t('admin:payments.table.headers.memberType')}
                              secondary={selectedPayment.user.memberType.name}
                            />
                          </ListItem>
                        )}
                      </List>
                    ) : (
                      <Typography color="text.secondary">User information not available</Typography>
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Payment Method Specific Details */}
                {selectedPayment.creditCardDetails && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Credit Card Details
                    </Typography>
                    <List dense>
                      {selectedPayment.creditCardDetails.transactionId && (
                        <ListItem>
                          <ListItemText
                            primary={t('admin:payments.table.headers.transactionId')}
                            secondary={selectedPayment.creditCardDetails.transactionId}
                          />
                        </ListItem>
                      )}
                      {selectedPayment.creditCardDetails.cardLastFour && (
                        <ListItem>
                          <ListItemText
                            primary={t('admin:payments.table.headers.card')}
                            secondary={`****${selectedPayment.creditCardDetails.cardLastFour} (${selectedPayment.creditCardDetails.cardBrand})`}
                          />
                        </ListItem>
                      )}
                      {selectedPayment.creditCardDetails.paymentGateway && (
                        <ListItem>
                          <ListItemText
                            primary={t('admin:payments.table.headers.gateway')}
                            secondary={selectedPayment.creditCardDetails.paymentGateway.toUpperCase()}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}

                {selectedPayment.wireTransferDetails && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Wire Transfer Details
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary={t('admin:payments.table.headers.bank')}
                          secondary={selectedPayment.wireTransferDetails.bankName}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('admin:payments.table.headers.referenceCode')}
                          secondary={selectedPayment.wireTransferDetails.referenceCode}
                        />
                      </ListItem>
                      {selectedPayment.wireTransferDetails.verifiedAt && (
                        <ListItem>
                          <ListItemText
                            primary={t('admin:payments.table.headers.verifiedAt')}
                            secondary={new Date(selectedPayment.wireTransferDetails.verifiedAt).toLocaleString()}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}

                {selectedPayment.attempts && selectedPayment.attempts.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Payment Attempts ({selectedPayment.attempts.length})
                    </Typography>
                    {selectedPayment.attempts.map((attempt, index) => (
                      <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Attempt {index + 1}:</strong> {attempt.status} - {new Date(attempt.attemptedAt).toLocaleString()}
                        </Typography>
                        {attempt.errorMessage && (
                          <Typography variant="body2" color="error">
                            Error: {attempt.errorMessage}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default AdminPaymentsPage;
